import {
  Alert,
  AlertDescription,
  AlertStatus,
  Box,
  CloseButton,
  createToastFn,
  Flex,
  Icon,
  StyleProps,
  ToastPosition,
  ToastProps,
} from '@chakra-ui/react';

import { Button } from '../button/button';
import { getStatusColorScheme, getStatusIcon, getTextColor } from '../utils';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastChakraProps extends ToastProps {
  description?: string;
  isClosable?: boolean;
  status?: AlertStatus;
  variant?: 'solid' | 'subtle' | 'left-accent' | 'top-accent';
  action?: ToastAction;
}

export function Toast({
  id,
  status,
  description,
  onClose,
  variant = 'subtle',
  isClosable,
  action,
}: ToastChakraProps) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const baseIcon = getStatusIcon(status!);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const colorScheme = getStatusColorScheme(status!);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const textColor = getTextColor(status!);

  const ids = id
    ? {
        root: `toast-${id}`,
        description: `toast-${id}-description`,
      }
    : undefined;
  return (
    <Alert
      id={ids?.root}
      addRole={false}
      status={status}
      variant={variant}
      borderRadius="md"
      boxShadow="lg"
      paddingEnd={action ? 10 : 4}
      width="auto"
      minW={action ? '320px' : undefined}
    >
      <Box borderRadius="lg" display="flex" p={1} mr={4} bgColor={colorScheme}>
        <Icon as={baseIcon} color="white" />
      </Box>
      {action ? (
        <Flex alignItems="center" gap={3} flex="1">
          {description && (
            <AlertDescription
              id={ids?.description}
              display="block"
              flex="1"
              textColor={textColor}
            >
              {description}
            </AlertDescription>
          )}
          <Button
            size="sm"
            flexShrink={0}
            variant="outline"
            color={colorScheme}
            borderColor={colorScheme}
            _hover={{
              bg: 'white',
              borderColor: colorScheme,
              textColor: colorScheme,
            }}
            _active={{
              bg: colorScheme,
              color: 'white',
              borderColor: colorScheme,
            }}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        </Flex>
      ) : (
        <Box>
          {description && (
            <AlertDescription
              id={ids?.description}
              display="block"
              textColor={textColor}
            >
              {description}
            </AlertDescription>
          )}
        </Box>
      )}
      {isClosable && (
        <CloseButton
          size="sm"
          onClick={onClose}
          position="absolute"
          insetEnd={1}
          top={1}
        />
      )}
    </Alert>
  );
}

declare type CreateToastFnReturn = ReturnType<typeof createToastFn>;

export const showToast = (
  toast: CreateToastFnReturn,
  status: AlertStatus,
  message: string,
  idToast?: string,
  position?: ToastPosition,
  duration?: number,
  containerStyle?: StyleProps,
  action?: ToastAction
) => {
  toast({
    id: idToast,
    position: position ? position : 'bottom',
    duration: duration ? duration : 3000,
    containerStyle,
    render: () => (
      <Toast
        id={idToast}
        description={message}
        onClick={() => toast.closeAll()}
        status={status}
        action={action}
      />
    ),
  });
};

export default Toast;
